/** Load the biplanes.io SVG resource pack and draw its glyph sheets. */
export class BitmapFont {
  readonly glyphW: number;
  readonly glyphH: number;
  private readonly image: HTMLImageElement;
  private readonly chars: string;
  private readonly widths: number[];
  private readonly cols: number;
  readonly spaceAdvance = 4;
  constructor(image: HTMLImageElement, def: FontDef) {
    this.image = image;
    this.chars = def.chars;
    this.widths = def.widths;
    this.cols = def.cols;
    this.glyphW = def.glyph_w;
    this.glyphH = def.glyph_h;
  }
  charWidth(ch: string): number {
    if (ch === ' ') return this.spaceAdvance;
    const i = this.chars.indexOf(ch);
    return i < 0 ? 0 : this.widths[i]!;
  }
  measure(text: string, tracking = 0): number {
    let w = 0;
    for (const ch of text) w += this.charWidth(ch) + (ch === ' ' ? 0 : tracking);
    return w;
  }
  draw(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, tracking = 0): void {
    let cx = x;
    for (const ch of text) {
      if (ch === ' ') {
        cx += this.spaceAdvance;
        continue;
      }
      const i = this.chars.indexOf(ch);
      if (i < 0) continue;
      const sx = (i % this.cols) * this.glyphW;
      const sy = Math.floor(i / this.cols) * this.glyphH;
      const w = this.widths[i]!;
      ctx.drawImage(this.image, sx, sy, w, this.glyphH, cx, y, w, this.glyphH);
      cx += w + tracking;
    }
  }
  wrap(text: string, width: number): string[] {
    if (width <= 0) return [];
    const lines: string[] = [];
    for (const paragraph of text.replace(/\\n/g, '\n').split('\n')) {
      let line = '';
      for (const word of paragraph.split(/ +/)) {
        if (!word) continue;
        if (line && this.measure(`${line} ${word}`) > width) {
          lines.push(line);
          line = '';
        }
        let remainder = word;
        while (this.measure(remainder) > width && remainder.length > 1) {
          if (line) {
            lines.push(line);
            line = '';
          }
          let end = 1;
          while (end < remainder.length && this.measure(remainder.slice(0, end + 1)) <= width)
            end++;
          lines.push(remainder.slice(0, end));
          remainder = remainder.slice(end);
        }
        line = line ? `${line} ${remainder}` : remainder;
      }
      lines.push(line);
    }
    return lines;
  }
  drawCentered(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    y: number,
    tracking = 0,
  ): void {
    this.draw(ctx, text, Math.round(cx - this.measure(text, tracking) / 2), y, tracking);
  }
}
export interface FontDef {
  source: string;
  cols: number;
  glyph_w: number;
  glyph_h: number;
  chars: string;
  widths: number[];
  image: string;
}
export interface ManifestSpriteInfo {
  group: string;
  w: number;
  h: number;
  valid: boolean;
}
export interface Manifest {
  engine_timing: {
    step_ms: number;
  };
  sprites: Record<string, ManifestSpriteInfo>;
  media: {
    type: string;
    size?: number;
  }[];
  trailer: {
    segments: [number, number][];
    segment_kinds: number[];
  };
}
export interface Strings {
  tm: string[];
  tt: string[];
  tg: string[];
}
export interface Assets {
  manifest: Manifest;
  strings: Strings;
  sprites: Map<string, HTMLImageElement>;
  fontMain: BitmapFont;
  fontRed: BitmapFont;
  fontBlue: BitmapFont;
}
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${url}`));
    img.src = url;
  });
}
async function loadJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return (await res.json()) as T;
}
export async function loadAssets(
  onProgress?: (done: number, total: number) => void,
): Promise<Assets> {
  const [manifest, strings, fontsJson] = await Promise.all([
    loadJson<Manifest>('/manifest.json'),
    loadJson<Strings>('/strings.json'),
    loadJson<Record<'main' | 'red' | 'blue', FontDef>>('/fonts.json'),
  ]);
  const names = Object.keys(manifest.sprites);
  const fontDefs: [keyof typeof fontsJson, string][] = [
    ['main', fontsJson.main.image],
    ['red', fontsJson.red.image],
    ['blue', fontsJson.blue.image],
  ];
  const total = names.length + fontDefs.length;
  let done = 0;
  const sprites = new Map<string, HTMLImageElement>();
  await Promise.all(
    names.map(async (name) => {
      const img = await loadImage(`/sprites/${name}`);
      sprites.set(name.replace(/\.(?:png|svg)$/, ''), img);
      onProgress?.(++done, total);
    }),
  );
  const fontImages: Record<string, HTMLImageElement> = {};
  await Promise.all(
    fontDefs.map(async ([key, path]) => {
      fontImages[key] = await loadImage(`/${path}`);
      onProgress?.(++done, total);
    }),
  );
  return {
    manifest,
    strings,
    sprites,
    fontMain: new BitmapFont(fontImages['main']!, fontsJson.main),
    fontRed: new BitmapFont(fontImages['red']!, fontsJson.red),
    fontBlue: new BitmapFont(fontImages['blue']!, fontsJson.blue),
  };
}
